import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Users, Clock, Calendar, MapPin, Star, CheckCircle,
  Brain, Mic, Music, Palette, Trophy, FlaskConical, BookOpen, Globe, Target,
  ChevronRight, Mail, Phone, Award, Heart, Play, Video, Code, ExternalLink, X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.15),0_8px_16px_rgba(0,0,0,0.1)] hover:shadow-[0_4px_0_rgba(0,0,0,0.15),0_6px_12px_rgba(0,0,0,0.08)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.15),0_2px_4px_rgba(0,0,0,0.05)] active:translate-y-[5px] transition-all duration-150 ease-out";

const BackButton = ({ onClick }) => (
  <button onClick={onClick}
    className={`inline-flex items-center gap-2 px-5 py-3 bg-white text-[#002147] font-semibold rounded-xl border-2 border-[#002147]/20 ${btn3D} cursor-pointer select-none`}
    data-testid="back-btn-bottom">
    <ArrowLeft size={18} /> Back to All Clubs
  </button>
);

const clubsData = {
  "chess-club": {
    name: "Chess Club",
    icon: Brain,
    color: "from-slate-700 to-slate-900",
    accentColor: "slate",
    image: "https://images.unsplash.com/photo-1765851802854-e577d9e6b48b?w=800&q=80",
    tagline: "Sharpen Your Mind, Master the Board",
    day: "Monday", time: "3:30 - 5:00 PM", location: "Main Campus - Room 12",
    eligibility: "Class 3 and above",
    description: "The YCPS Chess Club is one of our most prestigious intellectual clubs. We believe chess develops critical thinking, patience, strategic planning, and sportsmanship. Our members regularly compete in district and regional tournaments, bringing home trophies and recognition for the school.",
    activities: ["Weekly strategy lessons and practice games", "Monthly internal tournaments with prizes", "District & regional chess championship participation", "Chess puzzles and tactics training", "Annual YCPS Chess Championship"],
    achievements: ["2025 Western Region Junior Chess Champions", "5 students qualified for National Chess Olympiad", "Best School Chess Program - District Award 2024"],
    requirements: ["No prior experience needed - beginners welcome!", "Must commit to attending at least 3 sessions per month", "School chess set provided; bring your own if you have one"],
    advisor: { name: "[Advisor Name]", role: "Mathematics Teacher & Chess Master", email: "chess@ycpschool.com" },
    links: [
      { title: "Chess.com - Practice Online", url: "https://www.chess.com" },
      { title: "Lichess - Free Online Chess", url: "https://lichess.org" },
      { title: "Chess Puzzles for Beginners", url: "https://www.chesspuzzles.com" }
    ]
  },
  "debate-club": {
    name: "Debate Club",
    icon: Mic,
    color: "from-blue-700 to-blue-900",
    accentColor: "blue",
    image: "https://images.unsplash.com/photo-1660351174962-e2a1fbb9af09?w=800&q=80",
    tagline: "Speak with Confidence, Argue with Logic",
    day: "Tuesday", time: "3:30 - 5:00 PM", location: "Main Campus - Assembly Hall",
    eligibility: "Class 4 and above",
    description: "The Debate Club trains students to become articulate, logical, and persuasive speakers. Members learn the art of constructing arguments, rebutting opponents, and thinking on their feet. Our debaters have won multiple district championships and consistently represent YCPS at regional and national competitions.",
    activities: ["Weekly debate practice on current topics", "Public speaking and oratory workshops", "Inter-house and inter-class debate competitions", "Model United Nations (MUN) preparation", "Speech writing and presentation skills"],
    achievements: ["2025 District Debate Championship - 1st Place", "Best Speaker Award - Regional Finals 2025", "4 consecutive wins in Inter-School Debate League"],
    requirements: ["Open to all students Class 4 and above", "Interest in current affairs and reading", "Willingness to speak publicly and accept feedback"],
    advisor: { name: "[Advisor Name]", role: "English Language Teacher", email: "debate@ycpschool.com" },
    links: [
      { title: "Debate Topics for Students", url: "https://www.thoughtco.com/debate-topics-for-teens-8404553" },
      { title: "Public Speaking Tips", url: "https://www.toastmasters.org/resources/public-speaking-tips" },
      { title: "BBC Learning English", url: "https://www.bbc.co.uk/learningenglish" }
    ]
  },
  "drama-theatre-club": {
    name: "Drama & Theatre Club",
    icon: Star,
    color: "from-pink-600 to-rose-700",
    accentColor: "pink",
    image: "https://images.pexels.com/photos/6896324/pexels-photo-6896324.jpeg?auto=compress&w=800",
    tagline: "The Stage is Yours - Act, Create, Inspire",
    day: "Wednesday", time: "3:30 - 5:00 PM", location: "Annex Campus - Multi-Purpose Hall",
    eligibility: "All students welcome",
    description: "Our Drama & Theatre Club brings stories to life on stage! Students learn acting techniques, stagecraft, improvisation, and scriptwriting. We produce plays for school events, cultural celebrations, and inter-school drama festivals. Whether you want to act, direct, design sets, or manage backstage, there's a role for everyone.",
    activities: ["Acting techniques and improvisation workshops", "Annual school play production", "Cultural Day and Speech Day performances", "Scriptwriting and storytelling sessions", "Inter-school drama festival participation", "Costume and set design workshops"],
    achievements: ["Best School Play - District Drama Festival 2025", "3 students selected for Regional Youth Theatre", "Outstanding Performance - Ghana Schools Drama Competition"],
    requirements: ["No acting experience required", "Enthusiasm and willingness to perform", "Attend rehearsals regularly, especially before performances"],
    advisor: { name: "[Advisor Name]", role: "Creative Arts Teacher", email: "drama@ycpschool.com" },
    links: [
      { title: "Drama Games for Kids", url: "https://www.bbbpress.com/dramagames/" },
      { title: "Theatre for Young Audiences", url: "https://www.assitej-international.org/" },
      { title: "Scriptwriting for Beginners", url: "https://www.bbc.co.uk/writersroom/opportunities" }
    ]
  },
  "music-club": {
    name: "Music Club",
    icon: Music,
    color: "from-purple-600 to-violet-700",
    accentColor: "purple",
    image: "https://images.pexels.com/photos/31762320/pexels-photo-31762320.jpeg?auto=compress&w=800",
    tagline: "Find Your Rhythm, Share Your Melody",
    day: "Thursday", time: "3:30 - 5:00 PM", location: "Main Campus - Music Room",
    eligibility: "All students welcome",
    description: "The Music Club is where young musicians develop their talents. We offer instruction in keyboard, guitar, drums, recorder, and vocal training. Members perform at assemblies, cultural events, and school celebrations. Our school choir and band have won numerous awards at district and regional competitions.",
    activities: ["Instrumental lessons (keyboard, guitar, drums, recorder)", "Vocal training and choir rehearsals", "School band practice and performances", "Music theory and composition classes", "Recording and music production basics", "Annual School Concert"],
    achievements: ["2025 Western Region Schools Choir Competition - Gold", "Best School Band - District Music Festival", "5 students passed ABRSM Grade examinations"],
    requirements: ["No musical experience needed - we teach from scratch!", "Instruments provided by the school", "Regular attendance required for performances"],
    advisor: { name: "[Advisor Name]", role: "Music Teacher & Choir Director", email: "music@ycpschool.com" },
    links: [
      { title: "Music Theory Lessons", url: "https://www.musictheory.net/" },
      { title: "Virtual Piano", url: "https://virtualpiano.net/" },
      { title: "Rhythm Games", url: "https://www.incredibox.com/" }
    ]
  },
  "art-craft-club": {
    name: "Art & Craft Club",
    icon: Palette,
    color: "from-orange-500 to-amber-600",
    accentColor: "orange",
    image: "https://images.unsplash.com/photo-1615909340810-3ec0e50f9e4d?w=800&q=80",
    tagline: "Create, Express, Inspire Through Art",
    day: "Friday", time: "3:30 - 5:00 PM", location: "Main Campus - Art Studio",
    eligibility: "All students welcome",
    description: "The Art & Craft Club unleashes creativity! Students explore painting, drawing, sculpture, weaving, batik tie-dye, bead-making, and more. Our artworks are displayed at school events and exhibitions. This club is perfect for students who love to create with their hands and express themselves visually.",
    activities: ["Painting and drawing classes (watercolor, acrylic, pencil)", "Traditional Ghanaian craft making (kente weaving, bead-making)", "Sculpture and clay modeling", "Batik and tie-dye workshops", "Art exhibitions every term", "Poster and banner design for school events"],
    achievements: ["Best Art Exhibition - District Schools Art Fair 2025", "Students' artworks displayed at Takoradi City Gallery", "Won National Creativity Award - Junior Category"],
    requirements: ["All materials provided by the school", "Wear old clothes or an apron during sessions", "Bring your imagination!"],
    advisor: { name: "[Advisor Name]", role: "Visual Arts Teacher", email: "art@ycpschool.com" },
    links: [
      { title: "Drawing Tutorials for Kids", url: "https://www.artforkidshub.com/" },
      { title: "African Art Gallery", url: "https://africa.si.edu/" },
      { title: "DIY Craft Ideas", url: "https://www.redtedart.com/" }
    ]
  },
  "tennis-club": {
    name: "Tennis Club",
    icon: Trophy,
    color: "from-green-600 to-emerald-700",
    accentColor: "green",
    image: "https://images.unsplash.com/photo-1725680647573-9737175af6c4?w=800&q=80",
    tagline: "Serve, Rally, Win - Tennis Excellence",
    day: "Monday & Wednesday", time: "4:00 - 5:30 PM", location: "Main Campus - Tennis Court",
    eligibility: "Class 3 and above",
    description: "The Tennis Club teaches fundamentals of tennis including serving, forehand, backhand, volleying, and match strategy. Our students train on the school court and compete in inter-school tournaments. Tennis builds fitness, coordination, discipline, and sportsmanship.",
    activities: ["Basic and advanced tennis coaching", "Singles and doubles match play", "Fitness and agility training", "Inter-school tennis tournaments", "Annual YCPS Tennis Championship"],
    achievements: ["District U-14 Tennis Championship - 2nd Place 2025", "3 students selected for Western Region Junior Tennis Team", "Best Sportsmanship Award 2025"],
    requirements: ["Proper sports attire and tennis shoes required", "Rackets and balls provided for beginners", "Physical fitness and willingness to train"],
    advisor: { name: "[Advisor Name]", role: "Physical Education Teacher", email: "tennis@ycpschool.com" },
    links: [
      { title: "Tennis Rules for Beginners", url: "https://www.itftennis.com/en/about-us/governance/rules-of-tennis/" },
      { title: "Tennis Drills for Juniors", url: "https://www.tennisdrills.tv/" },
      { title: "Ghana Tennis Federation", url: "https://ghanatennis.org/" }
    ]
  },
  "football-club": {
    name: "Football Club",
    icon: Trophy,
    color: "from-emerald-600 to-green-700",
    accentColor: "emerald",
    image: "https://images.pexels.com/photos/274422/pexels-photo-274422.jpeg?auto=compress&w=800",
    tagline: "Teamwork, Skill, and Passion on the Pitch",
    day: "Tuesday & Thursday", time: "4:00 - 5:30 PM", location: "Main Campus - Football Field",
    eligibility: "All students welcome",
    description: "Football is the heartbeat of YCPS sports! Our Football Club trains students in dribbling, passing, shooting, and tactical play. We field teams for inter-house competitions, friendly matches, and the prestigious inter-school football league. Football teaches teamwork, discipline, and resilience.",
    activities: ["Skills training (dribbling, passing, shooting)", "Tactical and positional play sessions", "Inter-house football league", "Inter-school football tournaments", "Fitness and conditioning", "Annual Football Gala Day"],
    achievements: ["2025 District Inter-School Football League Champions", "Top Scorer Award - Regional Tournament", "Fair Play Award - Western Region Schools Cup"],
    requirements: ["Football boots and shin guards recommended", "School provides balls and training equipment", "Good sportsmanship is mandatory"],
    advisor: { name: "[Advisor Name]", role: "Sports Master", email: "football@ycpschool.com" },
    links: [
      { title: "FIFA Learning Resources", url: "https://www.fifa.com/football-development" },
      { title: "Football Skills Tutorials", url: "https://www.youtube.com/results?search_query=football+skills+for+kids" },
      { title: "Ghana Football Association", url: "https://ghanafa.org/" }
    ]
  },
  "science-club": {
    name: "Science Club",
    icon: FlaskConical,
    color: "from-cyan-600 to-teal-700",
    accentColor: "cyan",
    image: "https://images.pexels.com/photos/5427865/pexels-photo-5427865.jpeg?auto=compress&w=800",
    tagline: "Explore, Experiment, Discover",
    day: "Wednesday", time: "3:30 - 5:00 PM", location: "Main Campus - Science Lab",
    eligibility: "Class 3 and above",
    description: "The Science Club is where curiosity meets hands-on experimentation. Members conduct exciting experiments, build models, prepare science fair projects, and explore the wonders of physics, chemistry, biology, and earth science. We make science fun, accessible, and real!",
    activities: ["Weekly hands-on experiments", "Science fair project mentorship", "Field trips to scientific institutions", "Guest lectures from scientists and engineers", "STEM week demonstrations", "Inter-school science quiz competitions"],
    achievements: ["2025 Regional Science Fair - 3 Gold, 2 Silver medals", "Best Science Project - National STEM Exhibition", "Produced 2 finalists for Ghana Science Olympiad"],
    requirements: ["Curiosity and willingness to experiment!", "Lab coats provided; safety goggles required", "Maintain a science journal for observations"],
    advisor: { name: "[Advisor Name]", role: "Head of Science Department", email: "science@ycpschool.com" },
    links: [
      { title: "Fun Science Experiments", url: "https://www.sciencefun.org/kidszone/experiments/" },
      { title: "National Geographic Kids", url: "https://kids.nationalgeographic.com/" },
      { title: "Science Buddies - Fair Projects", url: "https://www.sciencebuddies.org/" }
    ]
  },
  "literary-reading-club": {
    name: "Literary & Reading Club",
    icon: BookOpen,
    color: "from-amber-600 to-yellow-700",
    accentColor: "amber",
    image: "https://images.pexels.com/photos/10638214/pexels-photo-10638214.jpeg?auto=compress&w=800",
    tagline: "Read, Write, and Dream Beyond Pages",
    day: "Friday", time: "3:30 - 4:30 PM", location: "Main Campus - Library",
    eligibility: "All students welcome",
    description: "The Literary & Reading Club fosters a love for reading and writing. Members explore different genres, discuss books, write stories and poems, and participate in reading challenges. We host poetry recitals, story-telling competitions, and spelling bees that showcase literary talent.",
    activities: ["Book of the Month discussions", "Creative writing workshops (stories, poems, essays)", "Poetry recitals and spoken word performances", "Spelling Bee preparation and competitions", "Reading challenge with rewards", "Annual Creative Writing Competition"],
    achievements: ["District Spelling Bee Champions 2025", "Published student anthology - 'Young Voices of YCPS'", "Best Reading Programme Award - Ghana Library Authority"],
    requirements: ["Bring a notebook and pen for writing sessions", "Willingness to read at least 1 book per month", "Share your favorite books with fellow members!"],
    advisor: { name: "[Advisor Name]", role: "English Literature Teacher & Librarian", email: "literary@ycpschool.com" },
    links: [
      { title: "Project Gutenberg - Free Books", url: "https://www.gutenberg.org/" },
      { title: "African Storybook", url: "https://www.africanstorybook.org/" },
      { title: "Young Writers Hub", url: "https://www.youngwritershub.co.uk/" }
    ]
  },
  "environmental-club": {
    name: "Environmental Club",
    icon: Globe,
    color: "from-lime-600 to-green-700",
    accentColor: "lime",
    image: "https://images.unsplash.com/photo-1758585734153-132c8620c1bd?w=800&q=80",
    tagline: "Protect Our Planet, One Action at a Time",
    day: "Saturday", time: "8:00 - 10:00 AM", location: "Both Campuses",
    eligibility: "All students welcome",
    description: "The Environmental Club educates students about conservation, sustainability, and taking care of our planet. Members participate in tree planting, campus beautification, recycling projects, and community clean-ups. We raise environmental awareness through campaigns and celebrate World Environment Day with special activities.",
    activities: ["Tree planting and campus beautification", "Recycling and waste management projects", "Nature walks and eco-field trips", "Environmental awareness campaigns", "World Environment Day celebrations", "School garden maintenance"],
    achievements: ["Planted 200+ trees on both campuses", "Won District Green Schools Award 2025", "Established first school recycling station in Takoradi"],
    requirements: ["Wear comfortable clothes and closed shoes", "Bring gloves for planting activities (or use school-provided ones)", "A caring heart for the environment!"],
    advisor: { name: "[Advisor Name]", role: "Agricultural Science Teacher", email: "enviro@ycpschool.com" },
    links: [
      { title: "NASA Climate Kids", url: "https://climatekids.nasa.gov/" },
      { title: "National Geographic Environment", url: "https://www.nationalgeographic.com/environment/" },
      { title: "WWF - World Wildlife Fund", url: "https://www.worldwildlife.org/" }
    ]
  },
  "cadet-corps": {
    name: "Cadet Corps",
    icon: Target,
    color: "from-red-700 to-red-900",
    accentColor: "red",
    image: "https://images.unsplash.com/photo-1472691777234-99bdb20e973c?w=800&q=80",
    tagline: "Discipline, Leadership, Service",
    day: "Saturday", time: "7:00 - 10:00 AM", location: "Main Campus - Parade Ground",
    eligibility: "Class 4 and above",
    description: "The Cadet Corps develops discipline, leadership, teamwork, and a sense of civic responsibility. Cadets learn drill formations, first aid, map reading, and community service. Our cadets lead flag-raising ceremonies, maintain order during school events, and represent YCPS at national parades and ceremonies.",
    activities: ["Drill and marching practice", "Leadership and teamwork exercises", "First aid and basic survival skills", "Community service projects", "National day parade participation", "Camping and outdoor adventures"],
    achievements: ["Best Cadet Squad - Regional Independence Day Parade 2025", "Community Service Excellence Award", "3 cadets selected for National Cadet Camp"],
    requirements: ["Cadet uniform provided by the school", "Physical fitness and discipline", "Parental consent required for outdoor activities"],
    advisor: { name: "[Advisor Name]", role: "Cadet Commander", email: "cadets@ycpschool.com" },
    links: [
      { title: "Ghana Armed Forces Cadet Association", url: "https://gafrec.mil.gh/" },
      { title: "First Aid for Kids", url: "https://www.redcross.org/take-a-class/first-aid" },
      { title: "Leadership Skills for Youth", url: "https://www.youthleadership.net/" }
    ]
  },
  "ict-coding-club": {
    name: "ICT & Coding Club",
    icon: Brain,
    color: "from-violet-700 to-indigo-800",
    accentColor: "violet",
    image: "https://images.pexels.com/photos/5530437/pexels-photo-5530437.jpeg?auto=compress&w=800",
    tagline: "Code the Future, Build with Technology",
    day: "Thursday", time: "3:30 - 5:00 PM", location: "Main Campus - ICT Lab",
    eligibility: "Class 3 and above",
    description: "The ICT & Coding Club prepares students for the digital world. Younger members start with Scratch visual programming, while older students learn HTML, CSS, Python, and app development. Members also learn graphic design, digital content creation, and cyber safety. This club builds problem-solving skills and computational thinking.",
    activities: ["Scratch programming (beginners)", "Python coding (intermediate/advanced)", "Web design with HTML & CSS", "App development projects", "Graphic design and digital art", "Robotics integration with coding", "Cyber safety awareness"],
    richActivities: [
      {
        title: "Scratch Programming",
        image: "https://images.unsplash.com/photo-1587620962725-abab7fe55159?w=600&q=70",
        color: "from-orange-600 to-amber-700",
        levels: [
          { level: "Basic", what: "Scratch is a visual programming language made by MIT. Instead of typing code, you drag and drop colourful blocks to make characters move, dance, and talk. It's like building with digital LEGO!", learn: ["Drag-and-drop coding blocks", "Making characters (sprites) move and talk", "Creating simple animations", "Using repeat loops to do things again and again", "Making a character respond to clicks"], example: "Students made a 'Dancing Cat' animation using motion and sound blocks!" },
          { level: "Intermediate", what: "Now you go deeper — creating full games with scoring, multiple levels, and character interactions. You'll learn variables (keeping score), broadcasting messages between sprites, and building projects with multiple scenes.", learn: ["Variables — keeping score and tracking lives", "If/else conditions — making smart decisions", "Broadcasting — sprites talking to each other", "Cloning — creating copies of sprites", "Building a complete game with levels"], example: "Students built a 'Space Invaders' game with score tracking, 3 lives, and increasing difficulty!" },
          { level: "Advanced", what: "Master Scratch by creating complex projects — multiplayer games, interactive stories with branching paths, and simulations. You'll use lists (arrays), custom blocks (functions), and cloud variables for online features.", learn: ["Lists — storing collections of data", "Custom blocks — creating your own commands", "Pen extension — drawing with code", "Complex game mechanics (gravity, physics)", "Publishing and sharing projects on MIT Scratch"], example: "Teams built a 'Quiz Show' platform with 50+ questions, a leaderboard, and a custom scoring algorithm!" }
        ]
      },
      {
        title: "Python Coding",
        image: "https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=600&q=70",
        color: "from-blue-400 to-indigo-500",
        levels: [
          { level: "Basic", what: "Python is the world's most popular beginner programming language. It reads almost like English! You type simple instructions and the computer follows them. It's used by Google, YouTube, and NASA.", learn: ["Printing messages on screen (print 'Hello!')", "Variables — storing names, ages, and numbers", "Simple maths with code (adding, subtracting)", "Input — asking the user questions", "If/else — making the computer decide"], example: "Students built a 'Times Table Quiz' that asks maths questions and tells you if you're right or wrong!" },
          { level: "Intermediate", what: "Take your Python skills further with loops, functions, and working with files. You'll build real tools that automate tasks and handle data — the kind of programs professionals use every day.", learn: ["For and while loops — repeating tasks", "Functions — writing reusable blocks of code", "Lists and dictionaries — organising data", "Reading and writing text files", "Error handling — making crash-proof programs"], example: "Students built a 'Student Grade Calculator' that reads marks from a file and calculates averages automatically!" },
          { level: "Advanced", what: "Become a Python pro! Build real applications using libraries, APIs, and object-oriented programming. Create GUIs (graphical programs), work with databases, and build web scrapers.", learn: ["Object-Oriented Programming (classes & objects)", "Using pip to install powerful libraries", "Building GUIs with Tkinter", "Working with APIs (fetching weather, news)", "Introduction to web development with Flask"], example: "Students built a 'Weather Dashboard' app that fetches live weather data from an API and displays it in a GUI window!" }
        ]
      },
      {
        title: "Web Design (HTML & CSS)",
        image: "https://images.unsplash.com/photo-1621839673705-6617adf9e890?w=600&q=70",
        color: "from-green-700 to-emerald-800",
        levels: [
          { level: "Basic", what: "Every website you visit — Google, YouTube, your school website — is built with HTML and CSS. HTML creates the content (text, images) and CSS makes it look beautiful (colours, fonts, layout).", learn: ["Creating your first web page with HTML tags", "Adding headings, paragraphs, and images", "Using CSS to change colours and fonts", "Making clickable links and buttons", "Building a personal 'About Me' page"], example: "Each student built their own 'About Me' webpage with their photo, hobbies, and favourite subjects!" },
          { level: "Intermediate", what: "Build professional-looking websites with layouts, navigation menus, and responsive design. Learn Flexbox, Grid, and how to make sites look great on both phones and computers.", learn: ["CSS Flexbox — arranging items in rows and columns", "CSS Grid — building page layouts", "Navigation menus and footers", "Responsive design — mobile-friendly sites", "Forms — contact pages and sign-up forms"], example: "Students built a complete 'School Website' with homepage, about page, contact form, and photo gallery!" },
          { level: "Advanced", what: "Add JavaScript to make websites interactive. Build animations, image sliders, dynamic content, and connect to real data. Learn frameworks and deploy your site live on the internet.", learn: ["JavaScript basics — making pages interactive", "DOM manipulation — changing page content with code", "CSS animations and transitions", "Using Bootstrap/Tailwind for rapid design", "Deploying a website live on the internet"], example: "Teams built an interactive 'School Events Calendar' website with filtering, search, and live deployment!" }
        ]
      },
      {
        title: "App Development",
        image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&q=70",
        color: "from-purple-700 to-fuchsia-800",
        levels: [
          { level: "Basic", what: "Apps are the programs on phones and tablets — like WhatsApp, Calculator, and games. In this module, students learn how apps are designed, planned, and built step-by-step using simple visual tools.", learn: ["Designing app screens on paper (wireframing)", "Using MIT App Inventor to build Android apps", "Adding buttons, text fields, and images", "Making the app respond to taps", "Testing your app on a real phone"], example: "Students built a 'School Timetable' app that shows your schedule for each day of the week!" },
          { level: "Intermediate", what: "Build more complex apps with multiple screens, data storage, and sensors. Learn to use the phone's camera, GPS, and accelerometer. Create apps that remember data between sessions.", learn: ["Multi-screen navigation", "Storing data locally (TinyDB)", "Using phone sensors (camera, GPS, tilt)", "Lists and loops in App Inventor", "Building a complete multi-feature app"], example: "Students built a 'Homework Tracker' app with due dates, reminders, and a completion checklist!" },
          { level: "Advanced", what: "Move beyond visual tools to real coding frameworks. Learn React Native or Flutter basics to build cross-platform apps. Understand APIs, databases, and publishing to app stores.", learn: ["Introduction to React Native / Flutter", "Building UIs with code components", "Connecting to online databases (Firebase)", "API integration — fetching live data", "Preparing an app for the Play Store"], example: "Teams built a 'School News' app that pulls live announcements from the school website and sends push notifications!" }
        ]
      },
      {
        title: "Graphic Design & Digital Art",
        image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?w=600&q=70",
        color: "from-pink-700 to-rose-800",
        levels: [
          { level: "Basic", what: "Graphic design is about creating visual content — posters, logos, and artwork using computers. Digital artists use tools like Canva instead of pencils and paint to create stunning visuals.", learn: ["Creating posters and flyers with Canva", "Understanding colours, fonts, and layout", "Adding text, shapes, and images", "Photo cropping and basic editing", "Designing a class poster or birthday card"], example: "Students designed the official poster for the school's Annual Sports Day using Canva!" },
          { level: "Intermediate", what: "Level up with advanced design tools. Learn GIMP (free Photoshop alternative), create logos, social media graphics, and understand design principles like contrast, alignment, and hierarchy.", learn: ["GIMP basics — layers, selections, filters", "Logo design principles and creation", "Social media graphic templates", "Photo manipulation and retouching", "Design principles — contrast, alignment, repetition"], example: "Students redesigned the school logo and created a full social media brand kit for the school's Instagram page!" },
          { level: "Advanced", what: "Master digital illustration, motion graphics, and professional workflows. Create animations, video thumbnails, infographics, and learn about UI/UX design for websites and apps.", learn: ["Digital illustration with drawing tablets", "Infographic design — turning data into visuals", "Basic motion graphics and GIF creation", "UI/UX design — designing app screens", "Building a professional design portfolio"], example: "Students created an animated infographic about 'Water Conservation in Ghana' that was featured on the school TV!" }
        ]
      },
      {
        title: "Robotics & Coding",
        image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&q=70",
        color: "from-red-700 to-orange-800",
        levels: [
          { level: "Basic", what: "Robotics combines coding with building physical machines. Students program small robots to move, follow lines, and complete tasks — seeing their code come to life in the real world!", learn: ["Understanding how robots work (sensors, motors)", "Programming a robot to move forward and turn", "Using simple commands to control movement", "Making a robot follow a drawn line", "Obstacle detection with sensors"], example: "Students programmed robots to navigate from one end of the classroom to the other without bumping into chairs!" },
          { level: "Intermediate", what: "Build more complex robots with multiple sensors and autonomous behaviour. Learn about decision-making algorithms, PID control, and create robots that can solve mazes and challenges.", learn: ["Multiple sensor integration (light, distance, touch)", "Decision-making — robot chooses its own path", "Maze-solving algorithms", "Speed and motor control (PID basics)", "Robot challenge competitions"], example: "Teams built robots that solved a maze — the fastest robot won a trophy at the Takoradi Tech Fair!" },
          { level: "Advanced", what: "Enter the world of advanced robotics with Arduino/Raspberry Pi. Build IoT devices, autonomous vehicles, and robots controlled via Bluetooth or WiFi. Combine AI with robotics.", learn: ["Arduino/Raspberry Pi programming", "Building custom robot hardware", "Bluetooth and WiFi remote control", "IoT — connecting robots to the internet", "Introduction to computer vision for robots"], example: "Students built a 'Smart Greenhouse' robot that monitors temperature and humidity and waters plants automatically!" }
        ]
      },
      {
        title: "Cyber Safety",
        image: "https://images.unsplash.com/photo-1563986768609-322da13575f2?w=600&q=70",
        color: "from-teal-700 to-cyan-800",
        levels: [
          { level: "Basic", what: "The internet is amazing but can be dangerous. Cyber safety teaches you how to protect yourself online — creating strong passwords, spotting fake websites, and knowing what NOT to share with strangers.", learn: ["Creating strong passwords that hackers can't guess", "Spotting fake emails and scam messages", "What personal information to NEVER share online", "How to report cyberbullying", "Safe social media habits for young people"], example: "Students took the 'YCPS Cyber Safety Pledge' and earned Digital Citizen certificates!" },
          { level: "Intermediate", what: "Go deeper into digital security. Learn about phishing attacks, two-factor authentication, safe browsing habits, and how to evaluate whether online information is trustworthy.", learn: ["Phishing — recognising fake websites and emails", "Two-factor authentication setup", "Privacy settings on social media platforms", "Evaluating online sources — real vs fake news", "Digital footprint — what you post stays forever"], example: "Students conducted a 'Phishing Test' on the school network and presented their findings at assembly!" },
          { level: "Advanced", what: "Explore the world of cybersecurity professionally. Learn basic ethical hacking concepts, network security, encryption, and how organisations protect their data from cyber attacks.", learn: ["Introduction to ethical hacking concepts", "Network security basics (firewalls, VPNs)", "Encryption — how secret messages work", "Incident response — what to do after a breach", "Career paths in cybersecurity"], example: "Students participated in a 'Capture the Flag' cybersecurity challenge and the winning team presented at the regional STEM fair!" }
        ]
      }
    ],
    achievements: [],
    requirements: ["No prior coding experience needed", "School computers provided", "Bring a notebook for planning and pseudocode"],
    advisor: { name: "[Advisor Name]", role: "ICT Teacher & Software Developer", email: "ict@ycpschool.com" },
    linkCategories: [
      {
        category: "Interactive Platforms (Start Here!)",
        icon: "play",
        links: [
          { title: "Scratch by MIT — Drag & Drop Coding", url: "https://scratch.mit.edu/" },
          { title: "Code.org — Hour of Code", url: "https://code.org/" },
          { title: "Blockly Games — Puzzle Coding", url: "https://blockly.games/" },
          { title: "Grasshopper by Google — Learn JavaScript", url: "https://grasshopper.app/" },
          { title: "SoloLearn — Bite-Size Lessons", url: "https://www.sololearn.com/" },
          { title: "Tynker — Coding for Kids", url: "https://www.tynker.com/" }
        ]
      },
      {
        category: "Video Tutorials (YouTube)",
        icon: "video",
        links: [
          { title: "freeCodeCamp — Full Courses (Free)", url: "https://www.youtube.com/@freecodecamp" },
          { title: "CS Dojo — Python for Beginners", url: "https://www.youtube.com/@CSDojo" },
          { title: "Bro Code — Fun Coding Tutorials", url: "https://www.youtube.com/@BroCodez" },
          { title: "Programming with Mosh — Beginner Friendly", url: "https://www.youtube.com/@programmingwithmosh" },
          { title: "Clever Programmer — Project-Based", url: "https://www.youtube.com/@CleverProgrammer" },
          { title: "Traversy Media — Web Dev Crash Courses", url: "https://www.youtube.com/@TraversyMedia" }
        ]
      },
      {
        category: "Step-by-Step Courses (Free)",
        icon: "book",
        links: [
          { title: "Khan Academy Computing", url: "https://www.khanacademy.org/computing" },
          { title: "Google CS First — Classroom Coding", url: "https://csfirst.withgoogle.com/" },
          { title: "W3Schools — HTML, CSS, Python Tutorials", url: "https://www.w3schools.com/" },
          { title: "Codecademy — Interactive Coding", url: "https://www.codecademy.com/" },
          { title: "Harvard CS50 — Intro to Computer Science", url: "https://cs50.harvard.edu/" },
          { title: "freeCodeCamp.org — Full Curriculum", url: "https://www.freecodecamp.org/" }
        ]
      },
      {
        category: "Python Basics",
        icon: "code",
        links: [
          { title: "Python.org — Official Beginner Tutorial", url: "https://docs.python.org/3/tutorial/" },
          { title: "Automate the Boring Stuff (Free Book)", url: "https://automatetheboringstuff.com/" },
          { title: "Real Python — Beginner Guides", url: "https://realpython.com/" },
          { title: "Programiz — Python Step by Step", url: "https://www.programiz.com/python-programming" }
        ]
      },
      {
        category: "Web Design & HTML/CSS",
        icon: "globe",
        links: [
          { title: "W3Schools HTML Tutorial", url: "https://www.w3schools.com/html/" },
          { title: "W3Schools CSS Tutorial", url: "https://www.w3schools.com/css/" },
          { title: "MDN Web Docs — Learn Web Dev", url: "https://developer.mozilla.org/en-US/docs/Learn" },
          { title: "CSS Tricks — Tips & Tricks", url: "https://css-tricks.com/" }
        ]
      },
      {
        category: "For Young Kids (Ages 5-10)",
        icon: "star",
        links: [
          { title: "Code Monster — Interactive JavaScript", url: "http://www.crunchzilla.com/code-monster" },
          { title: "Scratch Jr — Coding for Ages 5-7", url: "https://www.scratchjr.org/" },
          { title: "Lightbot — Coding Puzzles", url: "https://lightbot.com/" },
          { title: "PBS Kids ScratchJr Activities", url: "https://pbskids.org/scratchjr/" }
        ]
      }
    ],
    links: [
      { title: "Scratch - Start Coding", url: "https://scratch.mit.edu/" },
      { title: "Code.org - Learn to Code", url: "https://code.org/" },
      { title: "Khan Academy Computing", url: "https://www.khanacademy.org/computing" },
      { title: "Codecademy", url: "https://www.codecademy.com/" }
    ]
  }
};

const ClubDetailPage = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [enrolled, setEnrolled] = useState(false);
  const [activeActivity, setActiveActivity] = useState(null);
  const [activeLevel, setActiveLevel] = useState(0);
  const club = clubsData[clubId];

  if (!club) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-bold text-[#002147] mb-3">Club Not Found</h2>
          <button onClick={() => navigate("/school-clubs")}
            className={`px-6 py-3 bg-[#FF6B00] text-white font-semibold rounded-xl ${btn3D}`}>
            <ArrowLeft size={16} className="inline mr-2" /> Back to All Clubs
          </button>
        </Card>
      </div>
    );
  }

  const Icon = club.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="club-detail-page">
      {/* Hero */}
      <div className={`bg-gradient-to-r ${club.color} text-white`}>
        <div className="max-w-5xl mx-auto px-4 py-6">
          <button onClick={() => navigate("/school-clubs")}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 ${btn3D} border border-white/20`}
            data-testid="back-btn-top">
            <ArrowLeft size={16} /> Back to All Clubs
          </button>
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center"><Icon size={28} /></div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold">{club.name}</h1>
                  <p className="text-white/80">{club.tagline}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <Badge className="bg-white/20 text-white text-sm px-3 py-1 flex items-center gap-1"><Calendar size={14} /> {club.day}</Badge>
                <Badge className="bg-white/20 text-white text-sm px-3 py-1 flex items-center gap-1"><Clock size={14} /> {club.time}</Badge>
                <Badge className="bg-white/20 text-white text-sm px-3 py-1 flex items-center gap-1"><MapPin size={14} /> {club.location}</Badge>
                <Badge className="bg-white/20 text-white text-sm px-3 py-1 flex items-center gap-1"><Users size={14} /> {club.eligibility}</Badge>
              </div>
            </div>
            <div className="w-full md:w-64 h-44 rounded-2xl overflow-hidden shadow-2xl flex-shrink-0">
              <img src={club.image} alt={club.name} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* About */}
        <Card className="shadow-lg" data-testid="club-about">
          <CardHeader><CardTitle className="text-[#002147] flex items-center gap-2"><BookOpen size={20} /> About This Club</CardTitle></CardHeader>
          <CardContent><p className="text-gray-700 leading-relaxed">{club.description}</p></CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Activities */}
          <Card className="shadow-lg" data-testid="club-activities">
            <CardHeader><CardTitle className="text-[#002147] flex items-center gap-2"><Star size={20} /> What We Do</CardTitle></CardHeader>
            <CardContent>
              {club.richActivities ? (
                <div className="space-y-2.5">
                  {club.richActivities.map((a, i) => (
                    <button key={i} onClick={() => { setActiveActivity(a); setActiveLevel(0); }}
                      className={`w-full text-left px-5 py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 group cursor-pointer select-none flex items-center gap-4 ${btn3D}`}
                      data-testid={`activity-card-${i}`}>
                      <span className="font-bold text-[#002147] text-base flex-1">{a.title}</span>
                      <ChevronRight size={20} className="text-[#002147]/60 group-hover:translate-x-1 transition-transform" />
                    </button>
                  ))}
                </div>
              ) : (
                <ul className="space-y-2">
                  {club.activities.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" /> {a}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Achievements */}
          {club.achievements && club.achievements.length > 0 && (
          <Card className="shadow-lg" data-testid="club-achievements">
            <CardHeader><CardTitle className="text-[#002147] flex items-center gap-2"><Award size={20} /> Achievements</CardTitle></CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {club.achievements.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <Trophy size={16} className="text-[#FF6B00] flex-shrink-0 mt-0.5" /> {a}
                  </li>
                ))}
              </ul>
              <div className="mt-4 p-3 bg-[#002147]/5 rounded-lg">
                <h4 className="text-xs font-bold text-[#002147] uppercase mb-2">Requirements</h4>
                <ul className="space-y-1">
                  {club.requirements.map((r, i) => (
                    <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                      <ChevronRight size={12} className="flex-shrink-0 mt-0.5" /> {r}
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Requirements - shown separately when no achievements */}
          {(!club.achievements || club.achievements.length === 0) && club.requirements && (
          <Card className="shadow-lg">
            <CardHeader><CardTitle className="text-[#002147] flex items-center gap-2"><CheckCircle size={20} /> Requirements</CardTitle></CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {club.requirements.map((r, i) => (
                  <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                    <ChevronRight size={14} className="flex-shrink-0 mt-0.5 text-[#FF6B00]" /> {r}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          )}
        </div>

        {/* Useful Links */}
        <Card className="shadow-lg" data-testid="club-links">
          <CardHeader><CardTitle className="text-[#002147] flex items-center gap-2"><Globe size={20} /> Useful Resources & Links</CardTitle></CardHeader>
          <CardContent>
            {club.linkCategories ? (
              <div className="space-y-6">
                {club.linkCategories.map((cat, ci) => {
                  const catIcons = { play: Play, video: Video, book: BookOpen, code: Code, globe: Globe, star: Star };
                  const CatIcon = catIcons[cat.icon] || Globe;
                  const catColors = [
                    "text-green-700", "text-red-600", "text-blue-700",
                    "text-purple-700", "text-orange-600", "text-pink-600"
                  ];
                  const catIconColors = [
                    "text-green-500", "text-red-500", "text-blue-500",
                    "text-purple-500", "text-orange-500", "text-pink-500"
                  ];
                  const catBgColors = [
                    "bg-blue-600", "bg-purple-600", "bg-pink-600",
                    "bg-blue-600", "bg-purple-600", "bg-pink-600"
                  ];
                  const catTextColors = [
                    "text-white", "text-white", "text-white",
                    "text-white", "text-white", "text-white"
                  ];
                  return (
                    <div key={ci} className={ci > 0 ? "mt-10" : "mt-2"}>
                      <h4 className={`text-sm font-bold text-white ${catBgColors[ci % catBgColors.length]} flex items-center gap-2 px-4 py-3 rounded-xl mb-4`}>
                        <CatIcon size={16} className="text-white" /> {cat.category}
                      </h4>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {cat.links.map((link, i) => (
                          <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                            className={`p-4 rounded-xl bg-white border-2 border-gray-100 text-left ${btn3D} cursor-pointer select-none block group hover:border-violet-200`}
                            data-testid={`resource-${ci}-${i}`}>
                            <span className="text-sm font-semibold text-[#002147] flex items-center gap-2">
                              <ExternalLink size={13} className="text-[#FF6B00] flex-shrink-0" />
                              <span className="truncate">{link.title}</span>
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {club.links.map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                    className={`p-4 rounded-xl bg-white border-2 border-gray-100 text-left ${btn3D} cursor-pointer select-none block`}
                    data-testid={`club-link-${i}`}>
                    <span className="text-sm font-semibold text-[#002147] flex items-center gap-2">
                      <ChevronRight size={14} className="text-[#FF6B00]" /> {link.title}
                    </span>
                  </a>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activity Detail Page */}
        {activeActivity && (
          <div className="fixed inset-0 bg-gradient-to-b from-yellow-400 to-orange-400 z-50 overflow-y-auto" data-testid="activity-detail-modal">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#002147] to-[#0a3d7a] text-white">
              <div className="max-w-4xl mx-auto px-4 py-6">
                <button onClick={() => setActiveActivity(null)}
                  className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3D} cursor-pointer select-none`}
                  data-testid="close-modal-btn">
                  <ArrowLeft size={16} /> Back to Club
                </button>
                <h1 className="text-2xl font-bold">{activeActivity.title}</h1>
                <p className="text-white/70 text-sm">Choose your level below</p>
              </div>
              {/* Large hero image */}
              <div className="w-full h-52 overflow-hidden">
                <img src={activeActivity.image} alt={activeActivity.title} className="w-full h-full object-cover" />
              </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
              {/* Level Pill Buttons */}
              <div className="flex gap-3 justify-center" data-testid="level-tabs">
                {activeActivity.levels.map((lv, li) => (
                  <button key={li} onClick={() => setActiveLevel(li)}
                    className={`px-6 py-3 rounded-2xl text-sm font-bold flex-1 max-w-[200px] ${
                      activeLevel === li
                        ? "bg-gradient-to-r from-blue-500 to-blue-600 text-[#002147]"
                        : "bg-white text-[#002147] border-2 border-blue-200"
                    } ${btn3D}`}
                    data-testid={`modal-tab-${li}`}>
                    {lv.level}
                  </button>
                ))}
              </div>

              {(() => {
                const lv = activeActivity.levels[activeLevel];
                return (
                  <div className="space-y-5">
                    {/* What is it */}
                    <Card className="shadow-lg">
                      <CardContent className="p-5">
                        <h3 className="font-bold text-[#002147] text-lg mb-2">What is this?</h3>
                        <p className="text-gray-600 leading-relaxed">{lv.what}</p>
                      </CardContent>
                    </Card>

                    {/* What you learn */}
                    <Card className="shadow-lg">
                      <CardContent className="p-5">
                        <h3 className="font-bold text-[#002147] text-lg mb-3">What You'll Learn</h3>
                        <ul className="space-y-2.5">
                          {lv.learn.map((item, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <div className="w-7 h-7 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-white text-xs font-bold">{i + 1}</span>
                              </div>
                              <span className="text-sm text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>

                    {/* Student Example */}
                    <Card className="shadow-lg border-l-4 border-[#FF6B00]">
                      <CardContent className="p-5">
                        <h4 className="font-bold text-[#002147] text-sm mb-1 flex items-center gap-1.5"><Star size={14} className="text-[#FF6B00]" /> Student Example</h4>
                        <p className="text-sm text-gray-600 italic">{lv.example}</p>
                      </CardContent>
                    </Card>

                    {/* Navigation */}
                    <div className="flex gap-3">
                      {activeLevel > 0 && (
                        <button onClick={() => setActiveLevel(activeLevel - 1)}
                          className={`flex-1 py-3 bg-white text-[#002147] font-semibold rounded-xl text-sm flex items-center justify-center gap-2 border-2 border-blue-200 ${btn3D}`}>
                          <ArrowLeft size={16} /> {activeActivity.levels[activeLevel - 1].level}
                        </button>
                      )}
                      {activeLevel < 2 && (
                        <button onClick={() => setActiveLevel(activeLevel + 1)}
                          className={`flex-1 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-[#002147] font-semibold rounded-xl text-sm flex items-center justify-center gap-2 ${btn3D}`}>
                          Next: {activeActivity.levels[activeLevel + 1].level} <ChevronRight size={16} />
                        </button>
                      )}
                    </div>

                    <button onClick={() => setActiveActivity(null)}
                      className={`w-full py-3 bg-[#002147] text-white font-semibold rounded-xl ${btn3D}`}>
                      <ArrowLeft size={14} className="inline mr-2" /> Back to Club Page
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Advisor & Contact */}
        <Card className="shadow-lg" data-testid="club-advisor">
          <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase text-gray-400 font-bold mb-1">Club Advisor</p>
              <p className="font-semibold text-[#002147]">{club.advisor.name}</p>
              <p className="text-sm text-gray-500">{club.advisor.role}</p>
              <a href={`mailto:${club.advisor.email}`} className="text-sm text-[#FF6B00] flex items-center gap-1 mt-1 hover:underline"><Mail size={12} /> {club.advisor.email}</a>
            </div>
            <div className="flex gap-3">
              <a href={`mailto:${club.advisor.email}`}
                className={`inline-flex items-center gap-2 px-5 py-3 bg-[#002147] text-white font-semibold rounded-xl ${btn3D} cursor-pointer select-none`}
                data-testid="contact-advisor-btn">
                <Mail size={16} /> Contact Advisor
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Enrollment */}
        <Card className={`shadow-lg border-2 ${enrolled ? "border-green-300 bg-green-50" : "border-[#FF6B00]/30 bg-[#FF6B00]/5"}`} data-testid="club-enrollment">
          <CardContent className="p-8 text-center">
            {enrolled ? (
              <>
                <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
                <h3 className="text-xl font-bold text-green-800 mb-2">Enrollment Submitted!</h3>
                <p className="text-sm text-green-600 mb-4">Your enrollment request for {club.name} has been submitted. The club advisor will confirm your membership soon.</p>
                <button onClick={() => setEnrolled(false)}
                  className={`px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-xl ${btn3D}`}>
                  Cancel Enrollment
                </button>
              </>
            ) : (
              <>
                <Heart size={48} className="mx-auto text-[#FF6B00] mb-3" />
                <h3 className="text-xl font-bold text-[#002147] mb-2">Join {club.name} Today!</h3>
                <p className="text-sm text-gray-600 mb-5 max-w-md mx-auto">Click the button below to enroll. The club advisor will confirm your membership and send you details about the next meeting.</p>
                <button onClick={() => setEnrolled(true)}
                  className={`px-8 py-4 bg-[#FF6B00] text-white font-bold text-lg rounded-xl ${btn3D} cursor-pointer select-none`}
                  data-testid="enroll-btn">
                  <Users size={20} className="inline mr-2" /> Enroll Now
                </button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Bottom Back Button */}
        <div className="text-center py-4">
          <BackButton onClick={() => navigate("/school-clubs")} />
        </div>
      </div>
    </div>
  );
};

export default ClubDetailPage;
